import React from "react";
import { View, StyleSheet } from "react-native";

import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { UnifiedSubmission } from "../../../graphql/generated/graphql";
import { isSubmissionLate } from "../../../utils/date";
import { formatSubmittedDate } from "../../../utils/date";

interface SubmissionRowProps {
  submission: UnifiedSubmission;
  onGradePress: (submission: UnifiedSubmission) => void;
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case "RUNNING":
      return { bg: "#fef3c7", text: "#92400e" };
    case "NEEDS_REVIEW":
    case "Needs Review":
      return { bg: "#fecaca", text: "#dc2626" };
    case "COMPLETED":
    case "Completed":
      return { bg: "#d1fae5", text: "#065f46" };
    case "RETAKE_ALLOWED":
      return { bg: "#e0f2fe", text: "#0369a1" };
    default:
      return { bg: "#f3f4f6", text: "#6b7280" };
  }
};

const SubmissionRow: React.FC<SubmissionRowProps> = ({ submission, onGradePress }) => {
  const getSubmissionTypeColor = (type: string) => {
    switch (type) {
      case "Assignment":
        return { bg: "#e0f2fe", text: "#0369a1" };
      case "Assessment":
        return { bg: "#f3e8ff", text: "#7c3aed" };
      default:
        return { bg: "#f3f4f6", text: "#6b7280" };
    }
  };

  const statusColors = getStatusColor(submission.status);
  const typeColors = getSubmissionTypeColor(submission.submissionType);

  const shouldCheckLate = submission.submissionType === "Assignment" || submission.submissionType === "Assessment";
  const isLate =
    shouldCheckLate &&
    isSubmissionLate({
      submittedDate: submission.submittedOn,
      dueDate: submission.dueDate,
      retakeDeadline: submission.retakeDeadline,
    });

  return (
    <View style={styles.tableRow}>
      {/* Student */}
      <View style={[styles.cell, styles.studentCell]}>
        <View style={styles.userInfo}>
          <View style={styles.userAvatar}>
            <CustomText
              text={submission.student.initials}
              size="xs"
              weight="bold"
              style={styles.userInitials}
              useTranslationText={false}
            />
          </View>
          <View style={styles.userDetails}>
            <CustomText text={submission.student.name} size="s" style={styles.userName} useTranslationText={false} />
            <CustomText text={submission.student.email} size="xs" style={styles.userEmail} useTranslationText={false} />
          </View>
        </View>
      </View>

      {/* Submission Type */}
      <View style={[styles.cell, styles.typeCell]}>
        <View style={[styles.badge, { backgroundColor: typeColors.bg }]}>
          <CustomText
            text={submission.submissionType}
            size="xs"
            style={[styles.typeText, { color: typeColors.text }]}
          />
        </View>
      </View>

      {/* Course */}
      <View style={[styles.cell, styles.courseCell]}>
        <CustomText text={submission.courseName} size="s" style={styles.textSecondary} useTranslationText={false} />
      </View>

      {/* Submission Title */}
      <View style={[styles.cell, styles.moduleCell]}>
        <CustomText
          text={submission.submissionTitle}
          size="s"
          style={styles.textSecondary}
          useTranslationText={false}
        />
      </View>

      {/* Submitted Date */}
      <View style={[styles.cell, styles.dateCell]}>
        <CustomText
          text={formatSubmittedDate(submission.submittedOn)}
          size="s"
          style={styles.textSecondary}
          useTranslationText={false}
        />
        {isLate && (
          <>
            <CustomSpacing type="vertical" size="xs" />
            <View style={styles.lateTag}>
              <CustomText text="Late submission" size="xs" style={styles.lateTagText} useTranslationText={false} />
            </View>
          </>
        )}
      </View>

      {/* Status */}
      <View style={[styles.cell, styles.statusCell]}>
        <View style={[styles.badge, { backgroundColor: statusColors.bg }]}>
          <CustomText text={submission.status} size="xs" style={[styles.statusText, { color: statusColors.text }]} />
        </View>
      </View>

      {/* Grade */}
      <View style={[styles.cell, styles.gradeCell]}>
        <CustomText
          text={submission.grade != null ? `${submission.grade}/${submission.maximumPoints}` : "—"}
          size="s"
          style={styles.gradeText}
          useTranslationText={false}
        />
      </View>

      {/* Action */}
      <View style={[styles.cell, styles.actionsCell]}>
        <CustomButton
          text="Grade"
          size="s"
          onPress={() => onGradePress(submission)}
          styleType="primary"
          style={styles.gradeButton}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  studentCell: {
    flex: 2.5,
  },
  typeCell: {
    flex: 1.5,
  },
  courseCell: {
    flex: 1.5,
  },
  moduleCell: {
    flex: 1,
  },
  dateCell: {
    flex: 1.5,
  },
  statusCell: {
    flex: 1.5,
  },
  gradeCell: {
    flex: 1,
  },
  actionsCell: {
    flex: 1,
    alignItems: "flex-start",
  },
  userInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  userInitials: {
    color: "#FFFFFF",
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    color: "#111827",
    marginBottom: 2,
  },
  userEmail: {
    color: "#6B7280",
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    alignSelf: "flex-start",
  },
  typeText: {
    textTransform: "capitalize",
  },
  textSecondary: {
    color: "#374151",
  },
  statusText: {
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  gradeText: {
    color: "#1E293B",
  },
  gradeButton: {
    minWidth: 80,
    height: 32,
  },
  lateTag: {
    backgroundColor: "#FEF2F2",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: "#FCA5A5",
  },
  lateTagText: {
    color: "#DC2626",
    fontWeight: "600",
    fontSize: 10,
  },
});

export default SubmissionRow;
